import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as DeviceStatusIcon } from '../../assets/icons/dot-circle.svg';
import { ReactComponent as ChargerIcon } from '../../assets/icons/sidebar/sidebar-device.svg';
import { ReactComponent as ExpandIcon } from '../../assets/icons/tables/expand-icon.svg';
import { ReactComponent as NewTabIcon } from '../../assets/icons/tables/new-tab.svg';
import DefaultApiLimit from '../../config/defaultLimit';
import {
  DeviceListingProperties,
  DeviceListingRequest,
} from '../../interface/device-management.interface';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { setSelectedDevice } from '../../redux/slices/DeviceManagementSlice';
import { setPageTitle } from '../../redux/slices/PageTitleSlice';
import {
  disableFiltersApplied,
  fetchAllDevices,
  fetchListChargingPointFilters,
  // setSelectedDevice,
  updateChargingPointFilters,
  setSortData,
} from '../../redux/slices/PersonalDeviceSlice';
import CustomRadioButton from '../../shared-components/custom-radio-button/CustomRadioButton';
import CustomTable from '../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../shared-components/table-side-panel/DeviceSidePanel';
import { GetParametersFromCommonFilters } from '../../shared-functions/CommonFilters';
import { permissionCheck } from '../../shared-functions/JWTDocodeFunction';

import DeviceViewModel from './components/DeviceViewModel';

interface DeviceModalViewProperties {
  openModal: boolean;
  chargingPointSn: string;
}

/**
 * Component to display list of devices
 * @returns
 */
function PersonalDeviceTable({
  currentPage,
  setCurrentPage,
}: {
  currentPage: string;
  setCurrentPage: (value: string) => void;
}) {
  const [deviceModalView, setDeviceModalView] = useState<DeviceModalViewProperties>({
    openModal: false,
    chargingPointSn: '',
  });
  const [searchParameters, setSearchParameters] = useSearchParams();
  const { t } = useTranslation('devices');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    devices,
    limit,
    page,
    loading,
    totalPages,
    totalResults,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.personalDevice);

  useEffect(() => {
    dispatch(setPageTitle({ title: t('chargers'), icon: <ChargerIcon />, count: totalResults }));
  }, [totalResults]);

  const parametersDeviceId = searchParameters.get('deviceId');
  /**
   * Function to open the device in a new browser tab.
   * @param deviceId Identifier of the device to open in a new tab.
   */
  const handleOpenDeviceInNewTab = (deviceId: string | any) => {
    const deviceDetails = devices.find((value: DeviceListingProperties) => value.id === deviceId);
    dispatch(
      setSelectedDevice({
        id: deviceDetails?.id,
        name: deviceDetails?.chargingPointName,
        chargingPointSn: deviceDetails?.chargingPointSn,
      }),
    );
    const url = `/ev-admin/device-management/device-expanded/${deviceDetails?.id}/device-details?searchBy=${deviceDetails?.chargingPointName}`;
    window.open(url, '_blank'); // opens in a new tab
  };
  /**
   * Function to view the device
   * @param deviceId
   */
  const handleExpandDevice = (deviceId: string | any) => {
    const deviceDetails = devices.find((value: DeviceListingProperties) => value.id === deviceId);
    dispatch(
      setSelectedDevice({
        id: deviceDetails?.id,
        name: deviceDetails?.chargingPointName,
        chargingPointSn: deviceDetails?.chargingPointSn,
      }),
    );
    navigate({
      pathname: `./device/${deviceDetails?.id}/device-details`,
      search: `?searchBy=${deviceDetails?.chargingPointName}`,
    });
  };

  /**
   * UI of Status
   * @param device
   * @returns
   */
  const renderStatus = (device: DeviceListingProperties) =>
    device?.isDeviceOffline !== undefined ? (
      <div>
        <div className={device.isDeviceOffline ? 'tag-danger-label' : 'tag-success-label'}>
          <DeviceStatusIcon />
          <span style={{ fontWeight: '700' }}>
            {device.isDeviceOffline ? t('offline') : t('online')}
          </span>
        </div>
      </div>
    ) : (
      <div />
    );

  const column = [
    {
      label: t('endConsumerName'),
      accessorKey: 'enConsumerName' as keyof DeviceListingProperties,
      sortAllow: false,
    },
    {
      label: t('chargerName'),
      accessorKey: 'chargingPointName' as keyof DeviceListingProperties,
      sortAllow: true,
      queryParamsKey: 'deviceId',
      sidePanelComponent: <DeviceSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('chargerModel'),
      accessorKey: 'chargePointModel' as keyof DeviceListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('chargerSn'),
      accessorKey: 'chargingPointSn' as keyof DeviceListingProperties,
      sortAllow: false,
      hideWhenSidePanelOpen: true,
    },
    {
      label: t('maxPowerOutput'),
      accessorKey: 'maximumOutputPower' as keyof DeviceListingProperties,
      sortAllow: true,
      /**
       * Data custom function
       * @param currentRow
       */
      customData: (currentRow: DeviceListingProperties) => (
        <div className="value-container">
          <p className="value">{currentRow.maximumOutputPower}</p>
          <span>{currentRow.maximumOutputPowerUnit}</span>
        </div>
      ),
    },
    {
      label: t('connector1'),
      accessorKey: 'connector1' as keyof DeviceListingProperties,
      sortAllow: false,
    },
    {
      label: t('connector2'),
      accessorKey: 'connector2' as keyof DeviceListingProperties,
      sortAllow: false,
    },
    {
      label: t('chargerStatus'),
      accessorKey: 'isDeviceOffline' as keyof DeviceListingProperties,
      customData: renderStatus,
      sortAllow: false,
    },
    // {
    //   label: 'Actions',
    //   accessorKey: '' as keyof DeviceListingProperties,
    //   columnWidth: 80,
    //   customData:
    //     /**
    //      * function for action buttons
    //      * @param currentRow
    //      * @returns
    //      */
    //     (currentRow: DeviceListingProperties) => (
    //       <div className="action-button-group">
    //         <div className="device-listing-actions-wrap">
    //           <span>
    //             <RefreshIcon onClick={handleSubmit} />
    //           </span>
    //           {(permissionCheck('delete_device') as any) && (
    //             <span
    //               role="presentation"
    //               onClick={(event) => handleDeleteDeviceWarning(currentRow, event)}>
    //               <DeleteIcon />
    //             </span>
    //           )}
    //         </div>
    //       </div>
    //     ),
    // },
  ];

  /**
   * Fetch the devices details in redus
   */
  const fetchData = (data: DeviceListingRequest) => {
    if (!loading) {
      const queryData = {
        ...data,
        deviceType: 'personal',
      };
      dispatch(fetchAllDevices(queryData));
    }
  };

  /**
   * Function to close modal
   */
  const handleCloseModal = () => {
    setDeviceModalView({
      ...deviceModalView,
      openModal: false,
    });
  };
  /**
   * Table row click functionality
   * @param id
   */
  const handleClickTableRow = (id: string) => {
    // Set or update the logId param
    if (searchParameters.has('stationId')) {
      searchParameters.delete('stationId');
    }
    searchParameters.set('deviceId', id);
    // Apply the updated search params to the URL
    setSearchParameters(searchParameters);
  };

  /**
   * Table pagination function
   * @param value
   */
  const pageNateLimitChange = (value: DeviceListingRequest) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...value, ...queryParameters, sortField, sortBy });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };
  /**
   * handleAvailabilityChange
   */
  const handleAvailabilityChange = (name: string, value: string) => {
    setCurrentPage(value);
    searchParameters.delete('deviceId');
    setSearchParameters(searchParameters);
  };
  useEffect(() => {
    if (isFilterUpdated || devices.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);

      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        sortField,
        sortBy,
      });

      dispatch(disableFiltersApplied());
    }
  }, [filters]);

  return (
    <div className="device-listing-wrap">
      <DeviceViewModel open={deviceModalView.openModal} handleCloseModal={handleCloseModal} />
      <div className="device-listing-container">
        <CustomTable
          dataKey="DeviceManagement"
          version="v2"
          header={column}
          data={devices}
          serialNo
          filter={{}}
          filterClearFunction={() => {}}
          filterFunction={() => {}}
          actionButton={false}
          height={window.innerHeight >= 700 ? 327 : 288}
          limit={limit}
          page={page}
          loading={loading}
          totalPages={totalPages}
          totalResults={totalResults}
          sortData={{ sortBy, sortField }}
          handleSortChange={handleSortFunction}
          pageLimitChange={(value: any) => pageNateLimitChange(value)}
          sizeChangeButtonRequired
          rowClickEvent={handleClickTableRow}
          filters={filters}
          filtersToDispatch={updateChargingPointFilters}
          filterListLoading={filterListLoading}
          filterListToDispatch={fetchListChargingPointFilters}
          refreshRequired
          handleRefresh={handleRefresh}
          rowClickSidePanel={<DeviceSidePanel fetchIdFrom="queryParams" />}
          rowClickParamsKey="deviceId"
          ReferenceCurrentPages={handleRefresh}
          hoverLabel={t('refresh')}
          sidePanelTopLeftContent={
            (parametersDeviceId as string | any) && (
              <div className="side-panel-header-left-icons">
                <div
                  role="presentation"
                  className="expand"
                  onClick={() => handleExpandDevice(searchParameters.get('deviceId'))}>
                  <ExpandIcon />
                </div>
                <div
                  role="presentation"
                  className="new-tab"
                  onClick={() => handleOpenDeviceInNewTab(searchParameters.get('deviceId'))}>
                  <NewTabIcon />
                </div>
              </div>
            )
          }
          rightTopContent={
            <div className="revenue-management-toggle-wrap">
              {(permissionCheck('device_web_socket_management') as any) &&
                currentPage === 'websocketManagement123' && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="blocked-websocket-btn"
                    onClick={() =>
                      window.open(
                        '/ev-admin/device-management/websocket-management/errorLogs',
                        '_blank',
                      )
                    }
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        window.open(
                          '/ev-admin/device-management/websocket-management/errorLogs',
                          '_blank',
                        );
                      }
                    }}>
                    <span className="icon">
                      <NewTabIcon />
                    </span>
                    <span className="text">{t('websocketManagement')}</span>
                  </div>
                )}
              <CustomRadioButton
                name=""
                value="commercial"
                label={t('commercialCharger')}
                selected={currentPage === 'commercial'}
                onChangeFunc={handleAvailabilityChange}
              />
              <CustomRadioButton
                name=""
                value="personal"
                label={t('personalCharger')}
                selected={currentPage === 'personal'}
                onChangeFunc={handleAvailabilityChange}
              />
            </div>
          }
        />
      </div>
    </div>
  );
}

export default PersonalDeviceTable;
